import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { RouterService } from "../../../../shared/_services/router.service";
import {
    UserListInfo,
    UsersService,
    ListUsersResponse,
    UserTokenResponse,
    UsersSortOrder as UsersSortOrders,
} from "./users.service";
import { PageEvent } from "@angular/material/paginator";
import { APP_URL } from "../_components/left-menu/left-menu.component";
import { MatDialog } from "@angular/material/dialog";
import { SubscriptionEditDialogComponent } from "./subscription-edit-dialog/subscription-edit-dialog.component";
import { UserInfoDialogComponent } from "./user-info-dialog/user-info-dialog.component";
import {
    ConfirmDialogComponent,
    ConfirmDialogData,
} from "../../../../shared/_components/confirm-dialog/confirm-dialog.component";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-users",
    templateUrl: "./users.component.html",
    styleUrl: "./users.component.scss",
    standalone: false,
})
export class UsersComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();

    users: UserListInfo[] = [];
    page: number = 0;
    pageSize: number = 10;
    totalUsers: number = 0;
    isLoaded: boolean = false;
    keyword: string = "";
    sortOrder: UsersSortOrders = UsersSortOrders.Email;

    displayedColumns: string[] = [
        "id",
        "firstName",
        "lastName",
        "username",
        "email",
        "brokerAccountsCount",
        "botsCount",
        "createdAt",
        "actions",
    ];

    public sortOrders: number[] = Object.values(UsersSortOrders)
        .filter((value) => typeof value === "number")
        .map((value) => value as number);
    public UsersSortOrders = UsersSortOrders;

    constructor(
        private router: RouterService,
        private usersService: UsersService,
        @Inject(APP_URL) private readonly appUrl: string,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.pageChanged({ pageIndex: 0, pageSize: this.pageSize } as PageEvent);
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    pageChanged(e: PageEvent): void {
        this.pageSize = e.pageSize;
        this.usersService
            .list({
                page: e.pageIndex + 1,
                itemsPerPage: e.pageSize,
                keyword: this.keyword,
                sortOrder: this.sortOrder,
            })
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: ListUsersResponse) => {
                this.users = response.users;
                this.totalUsers = response.total;
                this.page = e.pageIndex;
                this.isLoaded = true;
            });
    }

    sortOrderChanged(): void {
        this.pageChanged({ pageIndex: 0, pageSize: this.pageSize } as PageEvent);
    }

    loginAs(userId: string): void {
        this.usersService
            .loginAs(userId)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: UserTokenResponse) => {
                window.location.href = `${this.appUrl}/signed-in/admin?token=${response.token}`;
            });
    }

    viewSubscriptions(userId: string): void {
        this.dialog.open(SubscriptionEditDialogComponent, { data: { userId }, width: "95vw", maxWidth: "700px" });
    }

    viewUserInfo(userId: string): void {
        // Wide enough for the bots' lock column; focus starts on the title, not on the first Lock button.
        this.dialog.open(UserInfoDialogComponent, {
            data: { userId },
            width: "95vw",
            maxWidth: "900px",
            autoFocus: "first-heading",
        });
    }

    deleteUser(userId: string): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "Delete User",
                message: "Are you sure you want to delete this user? This action cannot be undone.",
                type: "danger",
            } as ConfirmDialogData,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.usersService
                .delete(userId)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(() => {
                    this.pageChanged({ pageIndex: this.page, pageSize: this.pageSize } as PageEvent);
                });
        });
    }

    search(): void {
        this.pageChanged({ pageIndex: 0, pageSize: this.pageSize } as PageEvent);
    }
}
